import type { AxiosResponse } from 'axios'
import { api } from '@/services/customAxios'
import { base64ToBlob } from '@/utils/base64ToBlobConvert'

interface NumberRegistrationResponse {
  status: string
}

interface NumberBlockingResponse {
  result: string
}

interface StatusOfAnIndividualResponse {
  resident: boolean
}

interface IdentificationSimplifyResponse {
  pageType: string
  displayType: string
  url: string
}

interface IdentificationPersonPhotoResponse {
  title: string
}

interface RegistrationDeviceResponse {
  irsTaskId: string
}

interface CheckingRegistrationDeviceResponse {
  status: string
  description: string
}

interface Price {
  currency: string
  price: string
}

export interface ReplacementNumberCategoriesResponse {
  name: string
  id: number
  longDescription: string | null
  price: Price
}

export interface ContentItem {
  msisdn: string
  category_id: string
}

interface ReplacementNumbersResponse {
  content: ContentItem[]
  number: number
  size: number
  totalPages: number
  totalElements: number
  numberOfElements: number
  last: boolean
  first: boolean
}

export interface ReplacementNumberBody {
  category?: number
  mask?: string
  size?: number
}

export interface ReserveReplacementNumber {
  msisdn: string
  oldMsisdn: string
  categoryId: string
  clientVersion: string
  deviceOs: string
  deviceId: string
}

interface VerifyPinResponse {
  result: boolean
  attempt: number
  blockTime: string
  blockType: string
}

interface VerifyOtpResponse {
  verified: boolean
  attempt: number
  blockTime: string
  blockType: string
}

async function GetRegistrationDetails(
  iin: string,
  msisdn: string,
): Promise<AxiosResponse<string>> {
  return api.get<string>(`/web/v2/client/gov-data?iin=${iin}&msisdn=${msisdn}&simplify=true`)
}

async function GetRegistrationNumberResident(data: object) {
  return api.post(`/web/client/register`, data, {
    timeout: 60000,
  })
}

async function GetRegistrationNumberNonResident(data: object) {
  return api.post(`/web/register`, data, {
    timeout: 60000,
  })
}

async function CheckNumberRegistration(
  ctn: string,
): Promise<AxiosResponse<NumberRegistrationResponse>> {
  return api.get<NumberRegistrationResponse>(`/web/check/msisdn/${ctn}`)
}

async function CheckNumberBlocking(
  ctn: string,
): Promise<AxiosResponse<NumberBlockingResponse>> {
  return api.get<NumberBlockingResponse>(`/web/msisdn/${ctn}/blocking`)
}

async function CheckStatusOfAnIndividual(
  ctn: string,
): Promise<AxiosResponse<StatusOfAnIndividualResponse>> {
  return api.get<StatusOfAnIndividualResponse>(`/web/msisdn/${ctn}/document`)
}

async function GetIdentificationSimplify(
  photoBase64: string,
  body: object,
  sessionId: string,
): Promise<AxiosResponse<IdentificationSimplifyResponse>> {
  const formData = new FormData()
  await addPhotoToFormData(photoBase64, 'personPhoto', formData)

  for (const [key, value] of Object.entries(body)) {
    formData.append(key, value)
  }

  return api.post<IdentificationSimplifyResponse>(
    `/web/client/identification/simplify`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Session-ID': sessionId,
      },
    },
  )
}

async function GetIdentificationPersonPhoto(
  photoBase64: string,
  body: object,
  msisdn: string,
): Promise<AxiosResponse<IdentificationPersonPhotoResponse>> {
  const formData = new FormData()
  await addPhotoToFormData(photoBase64, 'personPhoto', formData)

  for (const [key, value] of Object.entries(body)) {
    if (value === null || value === undefined) {
      // skip null and undefined fields
    } else {
      formData.append(key, value as any)
    }
  }

  formData.append('msisdn', msisdn)

  return api.post<IdentificationPersonPhotoResponse>(
    `/web/identification/person-photo`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

async function GetIdentificationDocument(
  body: object,
  facePhoto: string,
  passportPhoto: string,
  msisdn: string,
): Promise<AxiosResponse<IdentificationPersonPhotoResponse>> {
  const formData = new FormData()
  await addPhotoToFormData(facePhoto, 'cropPhoto', formData)
  await addPhotoToFormData(passportPhoto, 'docPhoto', formData)

  for (const [key, value] of Object.entries(body)) {
    if (value === null || value === undefined) {
      // skip null and undefined fields
    } else {
      formData.append(key, value as any)
    }
  }

  formData.append('msisdn', msisdn)

  return api.post<IdentificationPersonPhotoResponse>(
    `/web/identification/document`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
}

async function addPhotoToFormData(
  photo: string,
  fieldName: string,
  formData: FormData,
) {
  const photoBlob = await base64ToBlob(
    photo.replace(/^data:image\/(png|jpeg);base64,/, ''),
    'image/jpeg',
  )
  const photoFile = new File([photoBlob], 'photo.jpeg', { type: 'image/jpeg' })
  formData.append(fieldName, photoFile)
}

async function GetRegistrationDevice(
  body: object,
): Promise<AxiosResponse<RegistrationDeviceResponse>> {
  return api.post<RegistrationDeviceResponse>(`/web/device/register`, body)
}

async function CheckRegistrationDevice(
  requestId: string,
): Promise<AxiosResponse<CheckingRegistrationDeviceResponse>> {
  return api.get<CheckingRegistrationDeviceResponse>(`/web/device/register/${requestId}`)
}

async function GetReplacementNumberList(
  body: ReplacementNumberBody,
): Promise<AxiosResponse<ReplacementNumbersResponse>> {
  const requestBody: Partial<ReplacementNumberBody> = {}

  if (body.mask) {
    requestBody.mask = body.mask
  }
  if (body.size) {
    requestBody.size = body.size
  }
  if (body.category) {
    requestBody.category = body.category
  }

  return api.post<ReplacementNumbersResponse>(`/number-change/numbers`, requestBody)
}

async function GetReplacementNumberCategories(): Promise<
  AxiosResponse<ReplacementNumberCategoriesResponse[]>
> {
  return api.get<ReplacementNumberCategoriesResponse[]>(`/number-change/categories`)
}

async function GetVerifyPin(
  body: object,
): Promise<AxiosResponse<VerifyPinResponse>> {
  return api.post<VerifyPinResponse>(`/web/check/pin`, body)
}

async function GetReserveReplacementNumber(body: ReserveReplacementNumber) {
  return api.post(`/number-change/start`, body)
}

async function GetCheckStatusReplacementNumber(ctn: string) {
  return api.get(`/number-change/status/${ctn}`)
}

async function GetNumberValidationReplacementNumber(ctn: string) {
  return api.get(`/number-change/validation?msisdn=${ctn}`)
}

async function GetSendOtpCode(
  msisdn: string,
): Promise<AxiosResponse<VerifyPinResponse>> {
  return api.post<VerifyPinResponse>(`/web/send/${msisdn}/otp`)
}

async function GetVerifyOtpCode(
  msisdn: string,
  body: object,
): Promise<AxiosResponse<VerifyOtpResponse>> {
  return api.post<VerifyOtpResponse>(`/web/verify/${msisdn}/otp`, body)
}

// GetPayForReplacementNumber

export {
  GetRegistrationDetails,
  GetRegistrationNumberResident,
  GetRegistrationNumberNonResident,
  CheckNumberRegistration,
  GetIdentificationSimplify,
  GetIdentificationPersonPhoto,
  GetIdentificationDocument,
  CheckNumberBlocking,
  CheckStatusOfAnIndividual,
  GetRegistrationDevice,
  CheckRegistrationDevice,
  GetReplacementNumberList,
  GetReplacementNumberCategories,
  GetReserveReplacementNumber,
  GetCheckStatusReplacementNumber,
  GetNumberValidationReplacementNumber,
  GetVerifyPin,
  GetSendOtpCode,
  GetVerifyOtpCode,
}
